import { useDrag } from 'react-dnd';
import styles from './SourceField.module.css';
import { DnDTypes, FieldTypes } from '@/types/types';
// import { fieldsLabels } from '@/constants';
import {
    ACharIcon,
    AddFileIcon,
    CalendarIcon,
    CheckmarkIcon,
    MultiLineIcon,
    SignatureIcon,
    StampIcon,
    TextIcon,
} from '../icons';
import { CSSProperties, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFieldsLables } from '@/hooks/useFieldsLabels';

export interface FieldProps {
    type: FieldTypes;
    className?: string;
    style?: CSSProperties;
    initialWidth?: number;
    initialHeight?: number;
    disabled?: boolean;
}

export const SOURCE_FIELD_WIDTH = 150;
export const SOURCE_FIELD_HEIGHT = 40;

export const SOFT_BLACK = '#212121';

const SourceField = ({
    type,
    style,
    disabled = false,
}: FieldProps) => {
    const { t } = useTranslation(['editFields', 'buttons', 'common']);
    const fieldsLabels = useFieldsLables();

    const [, drag] = useDrag(
        () => ({
            type: DnDTypes.SOURCE_FIELD,
            item: {
                type,
                width: SOURCE_FIELD_WIDTH,
                height: SOURCE_FIELD_HEIGHT,
            },
        }),
        [type]
    );

    const [isClicking, setIsClicking] = useState(false);

    const [showHelper, setShowHelper] = useState(false);

    const hasBeenDragged = useRef(false);
    const timerRef = useRef<number | null>(null);

    const handleOnMouseUp = () => {
        if (disabled) return;

        setIsClicking(false);

        if (!hasBeenDragged.current) {
            setShowHelper(true);

            timerRef.current = window.setTimeout(() => {
                setShowHelper(false);
            }, 3000);
        }
    };

    const handleOnMouseDown = () => {
        if (disabled) return;

        setIsClicking(true);

        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }

        hasBeenDragged.current = false;
        setShowHelper(false);
    };

    const handleOnDrag = () => {
        if (disabled) return;

        hasBeenDragged.current = true;
        setIsClicking(false);
    };

    const handleTouchStart = () => {
        if (disabled) return;

        setIsClicking(true);
    };

    const handleTouchEnd = () => {
        if (disabled) return;

        setIsClicking(false);
    };

    return (
        <div
            style={{
                width: SOURCE_FIELD_WIDTH,
                minHeight: SOURCE_FIELD_HEIGHT,
                height: SOURCE_FIELD_HEIGHT,
                ...style,
                transition: 'all .2s ease-in-out',
                transform: isClicking && !disabled ? 'scale(1.1)' : undefined,
                cursor: disabled ? 'not-allowed' : undefined,
                backgroundColor: disabled ? '#f0f0f0' : undefined,
                opacity: disabled ? 0.5 : 1,
            }}
            ref={disabled ? null : drag}
            className={styles.container}
            onDragStart={handleOnDrag}
            onMouseDown={handleOnMouseDown}
            onMouseUp={handleOnMouseUp}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            onContextMenu={(e) => {
                e.preventDefault();
            }}
        >
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    minWidth: '25px',
                    // backgroundColor: 'pink',
                }}
            >
                {type === FieldTypes.SIGNATURE && (
                    <SignatureIcon color={SOFT_BLACK} height={19} />
                )}
                {type === FieldTypes.TEXT && (
                    <TextIcon color={SOFT_BLACK} height={16} />
                )}
                {type === FieldTypes.STAMP && (
                    <StampIcon color={SOFT_BLACK} height={19} />
                )}
                {type === FieldTypes.CHECKBOX && (
                    <CheckmarkIcon color={SOFT_BLACK} height={13} />
                )}
                {type === FieldTypes.FILE && (
                    <AddFileIcon color={SOFT_BLACK} height={18} />
                )}
                {type === FieldTypes.TEXTAREA && (
                    <MultiLineIcon color={SOFT_BLACK} height={19} />
                )}
                {type === FieldTypes.DATE && (
                    <CalendarIcon color={SOFT_BLACK} height={17} />
                )}
                {type === FieldTypes.INITIALS && (
                    <ACharIcon color={SOFT_BLACK} height={17} />
                )}
            </div>
            <span className={styles.title}>{fieldsLabels[type]}</span>
            {showHelper && (
                <div className={styles.helperContainer}>
                    <span>{t('howToDrag')}</span>
                </div>
            )}
        </div>
    );
};
export default SourceField;
